export const MAX_ITERATIONS = 1024;
export const SMOOTHING_STEPS = 3;
const LOG2_INV = 1 / Math.log(2);

export function parseJuliaC(juliaC) {
  if (!Array.isArray(juliaC) || juliaC.length < 2) return null;
  const cx = Number(juliaC[0]);
  const cy = Number(juliaC[1]);
  if (!Number.isFinite(cx) || !Number.isFinite(cy)) return null;
  return [cx, cy];
}

export function juliaEscapeHorizon(juliaC) {
  const julia = parseJuliaC(juliaC);
  if (!julia) return 2;
  const absC = Math.hypot(julia[0], julia[1]);
  return Math.max(2, 0.5 * (1 + Math.sqrt(1 + 4 * absC)));
}

function complexSqrt(rx, ry) {
  const mag = Math.hypot(rx, ry);
  if (mag === 0) return [0, 0];
  const u = Math.sqrt(Math.max(0, 0.5 * (mag + rx)));
  const v = (ry < 0 ? -1 : 1) * Math.sqrt(Math.max(0, 0.5 * (mag - rx)));
  return [u, v];
}

function certifyCycleDisks(points, cx, cy) {
  const p = points.length;
  const norms = new Float64Array(p);
  const residuals = new Float64Array(p);
  let multiplier = 1;
  for (let k = 0; k < p; k++) {
    const [zx, zy] = points[k];
    const [nx, ny] = points[(k + 1) % p];
    const zMag = Math.hypot(zx, zy);
    if (!Number.isFinite(zMag) || zMag >= 2 - 1e-6) return null;
    norms[k] = zMag;
    multiplier *= 2 * zMag;
    const fx = zx * zx - zy * zy + cx;
    const fy = 2 * zx * zy + cy;
    const err = Math.hypot(fx - nx, fy - ny);
    if (!Number.isFinite(err) || err > 1e-10) return null;
    residuals[k] = err + 2 ** -42;
  }
  if (!(multiplier < 0.995)) return null;

  const disks = [];
  let primaryTrap = null;
  const contractsFrom = (startIndex, r) => {
    let rho = r;
    for (let step = 0; step < p; step++) {
      const idx = (startIndex + step) % p;
      if (norms[idx] + rho >= 2 - 2 ** -20) return false;
      rho = residuals[idx] + 2 * norms[idx] * rho + rho * rho + 2 ** -40;
      if (!Number.isFinite(rho)) return false;
    }
    return rho < r * (1 - 2 ** -16);
  };

  for (let k = 0; k < p; k++) {
    const minRadius = 1e-7;
    if (!contractsFrom(k, minRadius)) continue;
    let lo = minRadius;
    let hi = Math.max(minRadius, 2 - norms[k] - 1e-4);
    for (let iter = 0; iter < 28; iter++) {
      const mid = 0.5 * (lo + hi);
      if (contractsFrom(k, mid)) lo = mid;
      else hi = mid;
    }
    const radius = lo * 0.96;
    if (!(radius > 1e-6)) continue;
    const radiusSq = radius * radius;
    const [zx, zy] = points[k];
    const disk = Object.freeze({ x: zx, y: zy, radius, radiusSq, antipodal: false });
    disks.push(disk);
    if (!primaryTrap || radius > primaryTrap.radius) primaryTrap = disk;
    if (Math.hypot(zx, zy) > 1e-9) {
      disks.push(Object.freeze({ x: -zx, y: -zy, radius, radiusSq, antipodal: true }));
    }
  }
  if (!disks.length || !primaryTrap) return null;
  return Object.freeze({
    period: p,
    multiplier,
    points: Object.freeze(points.map(([x, y]) => Object.freeze([x, y]))),
    disks: Object.freeze(disks),
    primaryTrap,
  });
}

const attractingCycleCache = new Map();

export function findJuliaAttractingCycle(juliaC) {
  const julia = parseJuliaC(juliaC);
  if (!julia) return null;
  const [cx, cy] = julia;
  const key = `${cx},${cy}`;
  if (attractingCycleCache.has(key)) return attractingCycleCache.get(key);
  if (attractingCycleCache.size >= 64) {
    attractingCycleCache.delete(attractingCycleCache.keys().next().value);
  }

  let cycle = null;
  // Check period-1 fixed point: z^2 - z + c = 0 => z* = (1 - sqrt(1 - 4c)) / 2
  const [u1, v1] = complexSqrt(1 - 4 * cx, -4 * cy);
  const z1x = 0.5 * (1 - u1);
  const z1y = -0.5 * v1;
  if (2 * Math.hypot(z1x, z1y) < 0.995) {
    cycle = certifyCycleDisks([[z1x, z1y]], cx, cy);
  }
  // Check period-2 cycle: z^2 + z + c + 1 = 0 => p = (-1 +- sqrt(-3 - 4c)) / 2
  if (!cycle && 4 * Math.hypot(cx + 1, cy) < 0.995) {
    const [u2, v2] = complexSqrt(-3 - 4 * cx, -4 * cy);
    cycle = certifyCycleDisks([
      [0.5 * (-1 + u2), 0.5 * v2],
      [0.5 * (-1 - u2), -0.5 * v2],
    ], cx, cy);
  }
  // By Fatou's theorem, any attracting cycle of z^2 + c captures the critical orbit z0 = 0.
  if (!cycle && cx * cx + cy * cy <= 4) {
    const orbitX = new Float64Array(280);
    const orbitY = new Float64Array(280);
    let x = 0, y = 0, escaped = false;
    for (let i = 0; i < 280; i++) {
      orbitX[i] = x;
      orbitY[i] = y;
      const nx = x * x - y * y + cx;
      const ny = 2 * x * y + cy;
      x = nx;
      y = ny;
      if (x * x + y * y > 4) {
        escaped = true;
        break;
      }
    }
    if (!escaped) {
      const last = 279;
      for (let p = 1; p <= 8; p++) {
        if (Math.hypot(orbitX[last] - orbitX[last - p], orbitY[last] - orbitY[last - p]) < 1e-2) {
          let rx = orbitX[last], ry = orbitY[last];
          for (let step = 0; step < 12; step++) {
            let wx = rx, wy = ry, dx = 1, dy = 0;
            for (let j = 0; j < p; j++) {
              const ndx = 2 * (wx * dx - wy * dy);
              const ndy = 2 * (wx * dy + wy * dx);
              const nwx = wx * wx - wy * wy + cx;
              const nwy = 2 * wx * wy + cy;
              dx = ndx; dy = ndy; wx = nwx; wy = nwy;
            }
            const fx = wx - rx, fy = wy - ry;
            const dfx = dx - 1, dfy = dy;
            const denom = dfx * dfx + dfy * dfy;
            if (!(denom > 1e-24)) break;
            rx -= (fx * dfx + fy * dfy) / denom;
            ry -= (fy * dfx - fx * dfy) / denom;
          }
          const pts = [];
          let px = rx, py = ry;
          for (let j = 0; j < p; j++) {
            pts.push([px, py]);
            const nx = px * px - py * py + cx;
            const ny = 2 * px * py + cy;
            px = nx; py = ny;
          }
          cycle = certifyCycleDisks(pts, cx, cy);
          if (cycle) break;
        }
      }
    }
  }
  attractingCycleCache.set(key, cycle);
  return cycle;
}

export function traceJuliaOrbit(x0, y0, juliaC, maxSteps = 48) {
  const julia = parseJuliaC(juliaC);
  const cx = julia ? julia[0] : x0;
  const cy = julia ? julia[1] : y0;
  let x = julia ? x0 : 0;
  let y = julia ? y0 : 0;
  const points = [[x, y]];
  let escaped = x * x + y * y > 4;
  let escapeStep = escaped ? 0 : null;
  let minDistanceToOrigin = Math.hypot(x, y);
  let postEscapeSteps = 0;
  const limit = Math.max(1, Math.min(256, Math.floor(maxSteps)));
  for (let i = 1; i <= limit; i++) {
    const nx = x * x - y * y + cx;
    const ny = 2 * x * y + cy;
    x = nx;
    y = ny;
    const mag = Math.hypot(x, y);
    if (!Number.isFinite(mag)) break;
    points.push([x, y]);
    if (mag < minDistanceToOrigin) minDistanceToOrigin = mag;
    if (!escaped && mag > 2) {
      escaped = true;
      escapeStep = i;
    } else if (escaped) {
      postEscapeSteps++;
      if (postEscapeSteps >= 2 || mag > 16) break;
    }
  }
  const last = points[points.length - 1];
  return {
    points,
    escaped,
    escapeStep,
    finalMagnitude: Math.hypot(last[0], last[1]),
    minDistanceToOrigin,
  };
}

function isCardioidOrBulb(x0, y0) {
  const xm = x0 - 0.25;
  const y20 = y0 * y0;
  const q = xm * xm + y20;
  const xp1 = x0 + 1;
  return q * (q + xm) <= 0.25 * y20 || xp1 * xp1 + y20 <= 0.0625;
}

function finalizeEscape(result, iteration, x, y, x2, y2, x0, y0, useMu) {
  result.iterations = iteration;
  result.escaped = x2 + y2 > 4;
  result.mu = 0;
  if (result.escaped && useMu) {
    // Refine the escaped orbit before evaluating the continuous count. At
    // radius 2 alone, the approximation still produces visible contour seams.
    for (let i = 0; i < SMOOTHING_STEPS; i++) {
      y = 2 * x * y + y0;
      x = x2 - y2 + x0;
      x2 = x * x;
      y2 = y * y;
    }
    result.mu = iteration + SMOOTHING_STEPS + 1 - Math.log(0.5 * Math.log(x2 + y2)) * LOG2_INV;
  }
  return result;
}

/**
 * Compute backward preimages z_{n-1} = +-sqrt(z_n - c) starting from the
 * repelling fixed point z* = (1 + sqrt(1 - 4c)) / 2 on the Julia boundary J_c.
 * Combines a balanced binary tree seed with deterministic Halton-like branch
 * choices so sparse filament tips and Cantor dust clusters are both populated.
 */
export function computeInverseJuliaPreimages(juliaC, sampleCount = 12288) {
  const julia = parseJuliaC(juliaC) || [-0.8, 0.156];
  const [cx, cy] = julia;
  const count = Math.max(16, Math.min(65536, Math.floor(sampleCount)));
  const coords = new Float32Array(count * 2);
  const [u, v] = complexSqrt(1 - 4 * cx, -4 * cy);
  const startX = 0.5 * (1 + u);
  const startY = 0.5 * v;
  coords[0] = startX;
  coords[1] = startY;
  let written = 1;

  // Breadth-first binary tree expansion for the first 1023 preimages
  const treeLimit = Math.min(count, 1023);
  let parentIndex = 0;
  while (written < treeLimit) {
    const px = coords[parentIndex * 2];
    const py = coords[parentIndex * 2 + 1];
    parentIndex++;
    const [rx, ry] = complexSqrt(px - cx, py - cy);
    coords[written * 2] = rx;
    coords[written * 2 + 1] = ry;
    written++;
    if (written < treeLimit) {
      coords[written * 2] = -rx;
      coords[written * 2 + 1] = -ry;
      written++;
    }
  }

  // Deterministic low-discrepancy walk from the tree leaves for remaining samples
  let x = coords[(written - 1) * 2];
  let y = coords[(written - 1) * 2 + 1];
  let seed = 0x9e3779b9;
  for (; written < count; written++) {
    seed = (Math.imul(seed ^ (seed >>> 15), 0x85ebca6b) + 0xc2b2ae35) >>> 0;
    // Occasional jump back to a tree leaf prevents getting trapped in a high-density sink
    if ((written & 31) === 0 && treeLimit > 16) {
      const leaf = (seed % (treeLimit >> 1)) + (treeLimit >> 1);
      x = coords[leaf * 2];
      y = coords[leaf * 2 + 1];
    }
    const [rx, ry] = complexSqrt(x - cx, y - cy);
    const sign = (seed & 1) === 0 ? 1 : -1;
    x = sign * rx;
    y = sign * ry;
    coords[written * 2] = x;
    coords[written * 2 + 1] = y;
  }
  return coords;
}

/**
 * Accumulate an orbit-density (Buddhabrot / Nebulabrot) visit histogram for
 * escaping trajectories of z_{n+1} = z_n^2 + c across [xRange, yRange].
 */
export function computeJuliaOrbitDensityGrid(width, height, xRange, yRange, juliaC, maxIterations = 48) {
  const julia = parseJuliaC(juliaC) || [-0.8, 0.156];
  const [cx, cy] = julia;
  const w = Math.max(1, Math.floor(width));
  const h = Math.max(1, Math.floor(height));
  const counts = new Uint32Array(w * h);
  const xMin = xRange[0], xSpan = xRange[1] - xRange[0];
  const yMin = yRange[0], ySpan = yRange[1] - yRange[0];
  if (!(xSpan > 0 && ySpan > 0)) return { counts, maxCount: 0 };

  const traceX = new Float64Array(maxIterations);
  const traceY = new Float64Array(maxIterations);
  const sampleCols = Math.min(w, 120);
  const sampleRows = Math.min(h, 120);
  let maxCount = 0;

  for (let sy = 0; sy < sampleRows; sy++) {
    const y0 = yMin + ((sy + 0.5) / sampleRows) * ySpan;
    for (let sx = 0; sx < sampleCols; sx++) {
      const x0 = xMin + ((sx + 0.5) / sampleCols) * xSpan;
      let x = x0, y = y0;
      let steps = 0;
      let escaped = false;
      while (steps < maxIterations) {
        const nx = x * x - y * y + cx;
        const ny = 2 * x * y + cy;
        x = nx;
        y = ny;
        traceX[steps] = x;
        traceY[steps] = y;
        steps++;
        if (x * x + y * y > 4) {
          escaped = true;
          break;
        }
      }
      if (!escaped) continue;
      for (let k = 0; k < steps; k++) {
        const px = Math.floor(((traceX[k] - xMin) / xSpan) * w);
        const py = Math.floor(((traceY[k] - yMin) / ySpan) * h);
        if (px >= 0 && px < w && py >= 0 && py < h) {
          const idx = py * w + px;
          const next = ++counts[idx];
          if (next > maxCount) maxCount = next;
        }
      }
    }
  }
  return { counts, maxCount };
}

/**
 * Evaluate interior orbit-trap distance and convergence speed for bounded
 * points inside a filled Julia set K_c (Koenigs / Pickover stalk-and-trap
 * shading). Returns { escaped, trapDistance, convergenceStep, interiorPhase }.
 */
export function computeInteriorOrbitTrap(x0, y0, juliaC, maxIterations = 256) {
  const julia = parseJuliaC(juliaC) || [-0.8, 0.156];
  const [cx, cy] = julia;
  const cycle = findJuliaAttractingCycle(julia);
  const targetX = cycle?.primaryTrap?.x ?? 0;
  const targetY = cycle?.primaryTrap?.y ?? 0;
  const trapRadiusSq = cycle?.primaryTrap?.radiusSq ?? 1e-4;
  let x = x0, y = y0;
  let minDistSq = Infinity;
  let convergenceStep = maxIterations;
  for (let i = 0; i < maxIterations; i++) {
    if (x * x + y * y > 4) {
      return { escaped: true, trapDistance: 0, convergenceStep: i, interiorPhase: 0 };
    }
    const dx = x - targetX;
    const dy = y - targetY;
    const dSq = dx * dx + dy * dy;
    if (dSq < minDistSq) minDistSq = dSq;
    if (convergenceStep === maxIterations && dSq <= trapRadiusSq) {
      convergenceStep = i;
    }
    const nx = x * x - y * y + cx;
    const ny = 2 * x * y + cy;
    x = nx;
    y = ny;
  }
  const trapDistance = Number.isFinite(minDistSq) ? Math.sqrt(minDistSq) : 0;
  const interiorPhase = (convergenceStep * 0.15 - Math.log2(Math.max(1e-12, trapDistance))) % 1;
  return {
    escaped: false,
    trapDistance,
    convergenceStep,
    interiorPhase: interiorPhase < 0 ? interiorPhase + 1 : interiorPhase,
  };
}

// Shared by the main thread and workers. Keep the shader's equivalent in sync.
// A budget of N permits N recurrence steps and checks z_N for strict escape.
// The separate flag matters when escape occurs on the last permitted step.
export function iterateUntilEscape(x0, y0, result, maxIterations = MAX_ITERATIONS, useMu = true, juliaC = null) {
  const julia = parseJuliaC(juliaC);
  if (julia) {
    const cx = julia[0], cy = julia[1];
    const cycle = maxIterations >= 1 ? findJuliaAttractingCycle(julia) : null;
    const trap = cycle?.primaryTrap;
    let x = x0, y = y0, x2 = x * x, y2 = y * y, iteration = 0;
    if (trap) {
      const d0x = x - trap.x, d0y = y - trap.y;
      const a0x = x + trap.x, a0y = y + trap.y;
      if (d0x * d0x + d0y * d0y <= trap.radiusSq || a0x * a0x + a0y * a0y <= trap.radiusSq) {
        result.iterations = Math.ceil(maxIterations);
        result.escaped = false;
        result.mu = 0;
        return result;
      }
    }
    let periodicX = x0, periodicY = y0, periodicCheckpoint = 32;
    while (x2 + y2 <= 4 && iteration < maxIterations) {
      y = 2 * x * y + cy;
      x = x2 - y2 + cx;
      x2 = x * x;
      y2 = y * y;
      iteration++;
      if (trap) {
        const dx = x - trap.x, dy = y - trap.y;
        if (dx * dx + dy * dy <= trap.radiusSq) {
          result.iterations = Math.ceil(maxIterations);
          result.escaped = false;
          result.mu = 0;
          return result;
        }
      }
      if (iteration >= 32 && x === periodicX && y === periodicY &&
          Object.is(x, periodicX) && Object.is(y, periodicY) && x2 + y2 <= 4) {
        result.iterations = Math.ceil(maxIterations);
        result.escaped = false;
        result.mu = 0;
        return result;
      }
      if (iteration === periodicCheckpoint) {
        periodicX = x;
        periodicY = y;
        periodicCheckpoint *= 2;
      }
    }
    return finalizeEscape(result, iteration, x, y, x2, y2, cx, cy, useMu);
  }
  // Skip the known interior of the main cardioid and period-2 bulb. This keeps
  // the increased boundary detail budget inexpensive in overview images.
  if (isCardioidOrBulb(x0, y0)) {
    result.iterations = maxIterations;
    result.escaped = false;
    result.mu = 0;
    return result;
  }
  let x = 0, y = 0, x2 = 0, y2 = 0, iteration = 0;
  while (x2 + y2 <= 4 && iteration < maxIterations) {
    y = 2 * x * y + y0;
    x = x2 - y2 + x0;
    x2 = x * x;
    y2 = y * y;
    iteration++;
  }
  return finalizeEscape(result, iteration, x, y, x2, y2, x0, y0, useMu);
}

export function iterateWithDistance(x0, y0, result, maxIterations = MAX_ITERATIONS, useMu = true, juliaC = null) {
  const julia = parseJuliaC(juliaC);
  const cx = julia ? julia[0] : x0;
  const cy = julia ? julia[1] : y0;
  let x = julia ? x0 : 0;
  let y = julia ? y0 : 0;
  let x2 = x * x, y2 = y * y;
  let dx = julia ? 1 : 0, dy = 0;
  let iteration = 0;
  let periodicX = x, periodicY = y, periodicCheckpoint = 32;
  const cycle = julia && maxIterations >= 1 ? findJuliaAttractingCycle(julia) : null;
  const trap = cycle?.primaryTrap;
  if (!julia && isCardioidOrBulb(x0, y0)) {
    result.iterations = maxIterations;
    result.escaped = false;
    result.mu = 0;
    result.dem = 0;
    return result;
  }
  if (trap) {
    const d0x = x - trap.x, d0y = y - trap.y;
    const a0x = x + trap.x, a0y = y + trap.y;
    if (d0x * d0x + d0y * d0y <= trap.radiusSq || a0x * a0x + a0y * a0y <= trap.radiusSq) {
      result.iterations = Math.ceil(maxIterations);
      result.escaped = false;
      result.mu = 0;
      result.dem = 0;
      return result;
    }
  }
  while (x2 + y2 <= 4 && iteration < maxIterations) {
    const ndx = 2 * (x * dx - y * dy) + (julia ? 0 : 1);
    const ndy = 2 * (x * dy + y * dx);
    dx = ndx;
    dy = ndy;
    y = 2 * x * y + cy;
    x = x2 - y2 + cx;
    x2 = x * x;
    y2 = y * y;
    iteration++;
    if (trap) {
      const tx = x - trap.x, ty = y - trap.y;
      if (tx * tx + ty * ty <= trap.radiusSq) {
        result.iterations = Math.ceil(maxIterations);
        result.escaped = false;
        result.mu = 0;
        result.dem = 0;
        return result;
      }
    }
    if (iteration >= 32 && x === periodicX && y === periodicY &&
        Object.is(x, periodicX) && Object.is(y, periodicY) && x2 + y2 <= 4) {
      result.iterations = Math.ceil(maxIterations);
      result.escaped = false;
      result.mu = 0;
      result.dem = 0;
      return result;
    }
    if (iteration === periodicCheckpoint) {
      periodicX = x;
      periodicY = y;
      periodicCheckpoint *= 2;
    }
  }
  result.iterations = iteration;
  result.escaped = x2 + y2 > 4;
  result.mu = 0;
  result.dem = 0;
  if (result.escaped) {
    for (let i = 0; i < SMOOTHING_STEPS; i++) {
      const ndx = 2 * (x * dx - y * dy) + (julia ? 0 : 1);
      const ndy = 2 * (x * dy + y * dx);
      dx = ndx;
      dy = ndy;
      y = 2 * x * y + cy;
      x = x2 - y2 + cx;
      x2 = x * x;
      y2 = y * y;
    }
    const r2 = x2 + y2;
    const logR2 = Math.log(r2);
    if (useMu) {
      result.mu = iteration + SMOOTHING_STEPS + 1 - Math.log(0.5 * logR2) * LOG2_INV;
    }
    const dMag = Math.hypot(dx, dy);
    const rMag = Math.sqrt(r2);
    result.dem = dMag > 0 && Number.isFinite(dMag) ? (0.5 * rMag * logR2) / dMag : 0;
  }
  return result;
}

// Used only for compact views around the period-3 bulbs. The historical
// iterator above stays on the overview and exterior paths.
export function iterateUntilEscapePeriodic(x0, y0, result, maxIterations = MAX_ITERATIONS, useMu = true, juliaC = null) {
  if (parseJuliaC(juliaC)) return iterateUntilEscape(x0, y0, result, maxIterations, useMu, juliaC);
  if (isCardioidOrBulb(x0, y0)) {
    result.iterations = maxIterations;
    result.escaped = false;
    result.mu = 0;
    return result;
  }
  let x = 0, y = 0, x2 = 0, y2 = 0, iteration = 0;
  let periodicX = 0, periodicY = 0, periodicCheckpoint = 32;
  while (x2 + y2 <= 4 && iteration < maxIterations) {
    y = 2 * x * y + y0;
    x = x2 - y2 + x0;
    x2 = x * x;
    y2 = y * y;
    iteration++;
    if (iteration >= 32 && x === periodicX && y === periodicY &&
        Object.is(x, periodicX) && Object.is(y, periodicY) && x2 + y2 <= 4) {
      // An identical finite state and identical c give the same future orbit.
      // Object.is distinguishes signed zero; === excludes NaN.
      result.iterations = Math.ceil(maxIterations);
      result.escaped = false;
      result.mu = 0;
      return result;
    }
    if (iteration === periodicCheckpoint) {
      periodicX = x;
      periodicY = y;
      periodicCheckpoint *= 2;
    }
  }
  return finalizeEscape(result, iteration, x, y, x2, y2, x0, y0, useMu);
}
